import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

/**
 * The shell shared by the people and chat panels.
 *
 * On a tablet or desktop it is a column beside the video. On a phone it covers
 * the video entirely, because a 21rem column next to a 360px viewport would
 * leave the call a sliver — and someone reading the roster is not watching the
 * video at that moment anyway.
 *
 * The `panel` class names are a contract with the browser checks in scripts/.
 */
export function SidePanel({
  title,
  onClose,
  closeLabel,
  children,
  footer,
  error,
  bodyClassName,
}: {
  title: ReactNode;
  onClose: () => void;
  closeLabel: string;
  children: ReactNode;
  footer?: ReactNode;
  error?: string | null;
  bodyClassName?: string;
}) {
  return (
    <aside
      className={cn(
        'panel glass border-border/70 flex w-full min-w-0 shrink-0 flex-col border-l',
        'md:w-[19rem] lg:w-[21rem]',
        'max-md:absolute max-md:inset-0 max-md:z-30 max-md:border-l-0',
        'animate-in slide-in-from-right-4 fade-in-0 duration-200',
      )}
    >
      <header className="flex shrink-0 items-center justify-between gap-2 px-3 py-3 sm:px-4 sm:py-3.5">
        <h2 className="panel-title text-muted-foreground text-[11px] font-semibold tracking-[0.09em] uppercase">
          {title}
        </h2>
        <Button variant="ghost" size="icon-sm" aria-label={closeLabel} onClick={onClose}>
          <X className="size-4" />
        </Button>
      </header>

      <Separator />

      <div className={cn('flex min-h-0 flex-1 flex-col', bodyClassName)}>{children}</div>

      {error && (
        <p className="panel-error text-destructive shrink-0 border-t px-4 py-2.5 text-sm" role="alert">
          {error}
        </p>
      )}

      {footer && <div className="panel-footer shrink-0 border-t p-3">{footer}</div>}
    </aside>
  );
}
